import React from "react"

import { NotificationManager } from "react-notifications"
import { Redirect } from "react-router-dom"
import { gql, useQuery } from "urql"

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useTheme,
} from "@chakra-ui/core"

import { WithLoadingIndicator } from "~tailwindui/Basics"

import MicrophoneSelect from "components/AudioRoom/MicrophoneSelect"
import LocalTime from "components/LocalTime"

type TeacherJoinModalProps = {
  audioRoomId: string
  audioInputDevices: MediaDeviceInfo[]
  selectedAudioInputDeviceId: string
  setSelectedAudioInputDeviceId: (deviceId: string) => void
  microphoneError?: string
  onJoin: () => void
  joining: boolean
}

const TeacherJoinModal: React.FC<TeacherJoinModalProps> = ({
  audioRoomId,
  audioInputDevices,
  selectedAudioInputDeviceId,
  setSelectedAudioInputDeviceId,
  microphoneError,
  onJoin,
  joining,
}) => {
  const theme: any = useTheme()
  const [joinModalDetailsQueryResult] = useQuery({
    query: teacherJoinModalDetailsQuery,
    variables: { audioRoomId },
  })

  const roomNotAccessible =
    joinModalDetailsQueryResult.data &&
    !joinModalDetailsQueryResult.data.audioRoom

  if (roomNotAccessible) {
    NotificationManager.error("You do not have access to this room")
    return <Redirect to="/air-classrooms" />
  }

  return (
    <WithLoadingIndicator result={joinModalDetailsQueryResult}>
      {({ data: { audioRoom } }) => (
        <Modal isOpen isCentered closeOnOverlayClick={false} closeOnEsc={false}>
          <ModalContent>
            <ModalHeader>
              Join {audioRoom.activeAcrClassPeriod ? "Class" : "Your Office"}
            </ModalHeader>
            <ModalBody>
              {audioRoom.activeAcrClassPeriod && (
                <Stack spacing="1">
                  <Text>{audioRoom.activeAcrClassPeriod.acrClass.name}</Text>
                  <Text>
                    Subject:{" "}
                    {audioRoom.activeAcrClassPeriod.acrClass.subject.name}
                  </Text>
                  <Text>
                    <LocalTime
                      omitDate
                      timestamp={audioRoom.activeAcrClassPeriod.startsAt}
                    />
                    &nbsp;&mdash;&nbsp;
                    <LocalTime
                      omitDate
                      timestamp={audioRoom.activeAcrClassPeriod.endsAt}
                    />
                  </Text>
                </Stack>
              )}
              <label htmlFor="audio-input-device">
                <Text mt="4">Microphone</Text>
              </label>
              <MicrophoneSelect
                audioInputDevices={audioInputDevices}
                selectedAudioInputDeviceId={selectedAudioInputDeviceId}
                setSelectedAudioInputDeviceId={setSelectedAudioInputDeviceId}
                disabled={joining}
              />
              {microphoneError && (
                <Text color="red.500" mt="2" role="alert">
                  {microphoneError}
                </Text>
              )}
            </ModalBody>

            <ModalFooter justifyContent="center">
              <Button
                {...theme.buttonVariants.cloudroom}
                onClick={onJoin}
                isDisabled={Boolean(microphoneError) || joining}
                isLoading={joining}
                loadingText="Joining..."
                width="10rem"
              >
                Join
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </WithLoadingIndicator>
  )
}

const teacherJoinModalDetailsQuery = gql`
  query teacherJoinModalDetailsQuery($audioRoomId: ID!) {
    audioRoom(id: $audioRoomId) {
      teacher {
        id
        fullName
      }
      activeAcrClassPeriod {
        startsAt
        endsAt
        acrClass {
          name
          subject {
            name
          }
          teacher {
            fullName
          }
        }
      }
    }
  }
`

export default TeacherJoinModal
